import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  CheckCircle2, 
  Building2,
  Users,
  FileCheck,
  Shield,
  Briefcase,
  GraduationCap,
  Target,
  ArrowRight,
  MessageSquare,
  Award,
  Clock,
  BadgeCheck
} from 'lucide-react';

// Kostenträger / Auftraggeber
const fundingSources = [
  { name: 'Agentur für Arbeit', icon: Briefcase },
  { name: 'Jobcenter', icon: Users },
  { name: 'Deutsche Rentenversicherung', icon: Shield },
  { name: 'Berufsgenossenschaften', icon: BadgeCheck },
  { name: 'Berufsförderungsdienst der Bundeswehr', icon: Award }
];

// Was wir übernehmen
const services = [
  'Beratung zur Rechtsform und Struktur',
  'Aufbau des Qualitätsmanagementsystems',
  'Erstellung aller notwendigen Dokumente',
  'Vorbereitung auf das Zertifizierungsaudit',
  'Begleitung beim Audit',
  'Unterstützung nach der Zulassung'
];

// Ablauf der Gründungsbegleitung
const processSteps = [
  {
    number: '01',
    title: 'Erstgespräch',
    description: 'Wir klären Ihre Ausgangssituation, Ihre Ziele und den geplanten Leistungsumfang.'
  },
  {
    number: '02',
    title: 'Strukturaufbau',
    description: 'Gemeinsam entwickeln wir die passende Trägerstruktur und das QM-System.'
  },
  {
    number: '03',
    title: 'Dokumentation',
    description: 'Wir erstellen alle erforderlichen Unterlagen für die AZAV-Zulassung.'
  },
  {
    number: '04',
    title: 'Zertifizierungsaudit',
    description: 'Wir begleiten Sie durch das Audit bis zur erfolgreichen Zulassung.'
  }
];

// Voraussetzungen
const requirements = [
  'Fachliche Eignung im geplanten Bildungsbereich',
  'Gewerbeanmeldung oder Unternehmensform',
  'Räumlichkeiten für Schulungen (je nach Konzept)',
  'Bereitschaft zum Aufbau eines QM-Systems'
];

// Warum mit Begleitung gründen
const benefits = [
  { icon: Clock, text: 'Schnellerer Weg zur Zulassung' },
  { icon: Shield, text: 'Keine Fehler bei der Antragstellung' },
  { icon: FileCheck, text: 'Auditfeste Dokumentation von Anfang an' },
  { icon: Target, text: 'Klare Struktur statt Unsicherheit' },
  { icon: Award, text: 'Erfahrung aus erfolgreichen Zulassungen' }
];

// Warum MEHRAZAV
const trustReasons = [
  'Erfahrung aus eigenem AZAV-Trägerbetrieb',
  'Erfolgreiche Begleitung von Neugründungen',
  'Praxisnahe Lösungen statt Theorie',
  'Direkte und klare Kommunikation'
];

const BildungstraegerGruenden = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="bildungstraeger-gruenden-page">
      <Navbar />
      <div className="hero-glow" />
      <main className="pt-20 relative">
        
        {/* 1. Hero Section - Lebendiges Layout */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="text-primary text-sm font-semibold uppercase tracking-widest mb-6 block">
                  Für Gründer im Bildungsbereich
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight leading-[1.1] mb-8" data-testid="gruenden-headline">
                  Bildungsträger{' '}
                  <span className="text-primary">gründen</span>{' '}
                  und zugelassen werden
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-xl">
                  Von der ersten Idee bis zur erfolgreichen AZAV-Zertifizierung – wir begleiten Sie durch den gesamten Prozess.
                </p>
                <Link to="/kontakt">
                  <Button 
                    className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300"
                    data-testid="hero-cta-button"
                  >
                    Gründungsberatung anfragen
                  </Button>
                </Link>
              </motion.div>

              {/* Right Stats */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="hidden lg:block"
              >
                <div className="bg-card/20 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-6 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="text-4xl font-heading font-bold text-primary mb-2">50+</div>
                      <div className="text-sm text-muted-foreground">Träger begleitet</div>
                    </div>
                    <div className="text-center p-6 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="text-4xl font-heading font-bold text-primary mb-2">100%</div>
                      <div className="text-sm text-muted-foreground">Zulassungsquote</div>
                    </div>
                    <div className="text-center p-6 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="text-4xl font-heading font-bold text-primary mb-2">0</div>
                      <div className="text-sm text-muted-foreground">Abweichungen</div>
                    </div>
                    <div className="text-center p-6 bg-white/[0.02] rounded-xl border border-white/5">
                      <div className="text-4xl font-heading font-bold text-primary mb-2">5+</div>
                      <div className="text-sm text-muted-foreground">Jahre Erfahrung</div>
                    </div>
                  </div>
                  <p className="text-center text-muted-foreground text-sm mt-6 italic">
                    Praxiserfahrung aus eigenem Trägerbetrieb
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 2. Was ist ein Bildungsträger - LIGHT SECTION */}
        <section className="py-24 md:py-32 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight mb-8 text-gray-900">
                Was ist ein zugelassener Bildungsträger?
              </h2>
              <div className="space-y-5 text-gray-600 leading-relaxed text-lg">
                <p>
                  Ein zugelassener Bildungsträger ist ein Unternehmen, das Weiterbildungen, Umschulungen oder Coachings für Arbeitsuchende und Beschäftigte anbieten darf – finanziert durch öffentliche Mittel.
                </p>
                <p>
                  Um mit der <span className="text-gray-900 font-medium">Agentur für Arbeit</span>, dem <span className="text-gray-900 font-medium">Jobcenter</span>, der <span className="text-gray-900 font-medium">Deutschen Rentenversicherung</span> oder anderen Kostenträgern abrechnen zu können, benötigen Sie eine Zulassung nach AZAV (Akkreditierungs- und Zulassungsverordnung Arbeitsförderung).
                </p>
                <p className="text-gray-900 font-medium">
                  Ohne diese Zulassung ist eine Abrechnung mit öffentlichen Kostenträgern nicht möglich.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 3. Kostenträger / Mit wem Sie abrechnen können */}
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-white">
                Mit wem Sie als zugelassener Träger abrechnen können
              </h2>
            </motion.div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fundingSources.map((source, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="flex items-center gap-4 p-5 bg-card/30 border border-white/5 rounded-xl"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <source.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-white">{source.name}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. Was wir übernehmen - LIGHT SECTION */}
        <section className="py-24 md:py-32 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900">
                Was wir für Sie übernehmen
              </h2>
            </motion.div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <ul className="space-y-5">
                {services.map((service, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.08 }}
                    className="flex items-center gap-4"
                  >
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-900">{service}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 5. Ablauf der Gründungsbegleitung */}
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-white">
                So läuft die Gründungsbegleitung ab
              </h2>
            </motion.div>

            <div className="flex flex-col gap-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex gap-6 p-6 bg-card/30 border border-white/5 rounded-xl"
                >
                  <div className="text-3xl font-heading font-bold text-primary/40 flex-shrink-0 w-10">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-semibold text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Voraussetzungen - LIGHT SECTION */}
        <section className="py-24 md:py-32 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900">
                Was Sie mitbringen sollten
              </h2>
            </motion.div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <ul className="space-y-5">
                {requirements.map((req, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.08 }}
                    className="flex items-start gap-4"
                  >
                    <ArrowRight className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-gray-900">{req}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-gray-600 mt-8 text-center"
            >
              Nicht sicher, ob Sie die Voraussetzungen erfüllen? Lassen Sie uns darüber sprechen.
            </motion.p>
          </div>
        </section>

        {/* 7. Warum mit Begleitung gründen */}
        <section className="py-24 md:py-32 bg-[#050505]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-white">
                Warum eine professionelle Begleitung sinnvoll ist
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="flex items-center gap-4 p-5 bg-card/30 border border-white/5 rounded-xl"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-white">{benefit.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 8. Vertrauensabschnitt - LIGHT SECTION */}
        <section className="py-24 md:py-32 bg-gray-100">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="mb-12"
            >
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-gray-900">
                Warum MEHRAZAV
              </h2>
            </motion.div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <ul className="space-y-5">
                {trustReasons.map((reason, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.08 }}
                    className="flex items-center gap-4"
                  >
                    <Award className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-gray-900">{reason}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 9. Abschluss-CTA */}
        <section className="py-24 md:py-32 bg-[#050505] relative">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-8">
                <MessageSquare className="w-7 h-7 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight mb-6 text-white">
                Sie planen einen Bildungsträger zu gründen?
              </h2>
              <p className="text-muted-foreground leading-relaxed max-w-xl mx-auto mb-10 text-lg">
                Lassen Sie uns in einem kurzen Gespräch klären, ob und wie eine Zusammenarbeit sinnvoll ist. 
                Sie erhalten eine ehrliche Einschätzung zu Ihrem Vorhaben.
              </p>
              <Link to="/kontakt">
                <Button 
                  className="bg-primary hover:bg-[#05b873] text-black font-semibold rounded-lg px-8 py-6 text-lg transition-all duration-300"
                  data-testid="footer-cta-button"
                >
                  Gründungsberatung anfragen
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

      </main>
      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default BildungstraegerGruenden;
